import { effect, inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { AppSettings } from '@twist/core';
import { BreadcrumbService } from '@twist/ui/components';

@Injectable({ providedIn: 'root' })
export class PageTitleStrategy extends TitleStrategy {

  private readonly appSettings = inject(AppSettings);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly title = inject(Title);
  private lastSnapshot: RouterStateSnapshot | null = null;

  constructor() {
    super();

    effect(() => {
      this.breadcrumbService.customLabels();
      if (this.lastSnapshot) {
        this.setTitleFromSnapshot(this.lastSnapshot);
      }
    });
  }

  override updateTitle(routerState: RouterStateSnapshot) {
    this.lastSnapshot = routerState;
    this.setTitleFromSnapshot(routerState);
  }

  private setTitleFromSnapshot(routerState: RouterStateSnapshot): void {
    const title = this.getBreadcrumbTitle(routerState) ?? this.buildTitle(routerState);
    const prefix = this.appSettings.environment.pageTitlePrefix;
    if (title !== undefined) {
      this.title.setTitle(`${prefix}${title} | Twist`);
      return;
    }

    this.title.setTitle(`${prefix}Twist`);
  }

  private getBreadcrumbTitle(routerState: RouterStateSnapshot): string | undefined {
    const labels = this.buildBreadcrumbLabels(routerState.root);
    if (labels.length === 0) {
      return undefined;
    }

    return labels.slice().reverse().join(' | ');
  }

  private buildBreadcrumbLabels(
    routeSnapshot: ActivatedRouteSnapshot,
    labels: string[] = []
  ): string[] {
    const children: ActivatedRouteSnapshot[] = routeSnapshot.children;

    if (children.length === 0) {
      return labels;
    }

    for (const child of children) {
      if (child.outlet !== 'primary') {
        continue;
      }

      const label = this.breadcrumbService.getLabelForRoute(child);
      if (label) {
        labels.push(label);
      }

      return this.buildBreadcrumbLabels(child, labels);
    }

    return labels;
  }
}
